import type { DATA_SETS_ENUM_TYPES, UID_NAME_TUPLES } from './dataSets';
import type { STEPS_PROFIT_CENTER_MODAL } from './valueChain';

export const BUTTON_VARIANT = {
  primary: 'primary',
  secondary: 'secondary',
  cancel: 'cancel',
  action: 'action',
  error: 'error',
  ghost: 'ghost',
  openLink: 'openLink',
} as const;

export const ICON_BUTTON_SIZES = {
  small: 'small',
  medium: 'medium',
  large: 'large',
} as const;

export const USER_STATUS = {
  draft: 'draft',
  invited: 'invited',
  active: 'active',
  deactivated: 'deactivated',
} as const;

export const SEPARATOR_VARIANT = {
  vertical: 'vertical',
  horizontal: 'horizontal',
} as const;

export const STATUS = {
  enabled: 'enabled',
  disabled: 'disabled',
} as const;

export const PILLAR_ACCESS_TYPES = {
  invisible_disabled: 'invisible-disabled',
  dimmed_disabled: 'dimmed-disabled',
  visible_enabled: 'visible-enabled',
} as const;

export const COMPANY_STATUS = {
  draft: 'draft',
  active: 'active',
  deactivated: 'deactivated',
} as const;

export const VALUE_CHAIN_STATUS = {
  current: 'current',
  inProgress: 'in-progress',
  readyForReview: 'ready-for-review',
  underReview: 'under-review',
  requestApproval: 'request-approval',
  approved: 'approved',
  requiresRework: 'requires-rework',
  archived: 'archived',
  processing: 'processing',
} as const;

/** Status of a value chain impact analysis result (scenario run). */
export enum EVCAnalysisResultStatus {
  PROCESSING = 'processing',
  COMPLETED = 'completed',
  ERROR = 'error',
}

export const PRESIGNED_FILE_TYPES = {
  company_logo: 'company-logo',
  partner_logo: 'partner-logo',
  dataSet: 'data-set',
  task_attachment: 'task-attachment',
  questionnaire_attachment: 'questionnaire-attachment',
  valueChainAttachment: 'value-chain-attachment',
  materialityAssessmentImport: 'materiality-assessment-import',
} as const;

export const ORDERS = {
  asc: 'ASC',
  desc: 'DESC',
} as const;

export const USER_ROLE_TYPES = {
  admin: 'admin',
  companyAdmin: 'company-admin',
  facilitator: 'facilitator',
  contributor: 'contributor',
  auditor: 'auditor',
  signatory: 'signatory',
} as const;

export const THEME = {
  light: 'light',
  dark: 'dark',
};

export const INPUT_TYPES = {
  text: 'text',
  password: 'password',
  email: 'email',
  tel: 'tel',
};

export const TRANSLATE_STATUS = {
  idle: 'idle',
  loading: 'loading',
  failed: 'failed',
};

export const AUTHENTICATOR_SETUP_MODAL_STEPS = {
  phone: 'phone',
  smsCode: 'smsCode',
  authenticationSetup: 'authentication',
  downloadApp: 'downloadApp',
  qrCode: 'qrCode',
  secretCode: 'secretCode',
  verificationCode: 'verificationCode',
} as const;

export const MFA_SMS_SETUP_MODAL_STEPS = {
  phone: 'phone',
  smsCode: 'smsCode',
} as const;

export const MFA_ENDPOINTS = {
  startSyncWithApp: 'startSyncWithApp',
  endSyncWithApp: 'endSyncWithApp',
  verifyViaApp: 'verifyViaApp',
  startSyncWithPhone: 'startSyncWithPhone',
  endSyncWithPhone: 'endSyncWithPhone',
  verifyViaPhone: 'verifyViaPhone',
  sendSms: 'sendSms',
  loginInfo: 'loginInfo',
  newPassword: 'newPassword',
  forgotPasswordConfirm: 'forgotPasswordConfirm',
} as const;

export const USERS_ROLE = {
  admin: 'admin',
  companyAdmin: 'company-admin',
  administrator: 'administrator',
  facilitator: 'facilitator',
  contributor: 'contributor',
  auditor: 'auditor',
  signatory: 'signatory',
} as const;

export const USER_PILLAR_ACCESS_TYPES = {
  noAccess: 'no-access',
  viewOnly: 'view-only',
  viewEdit: 'view-edit',
  viewAudit: 'view-audit',
  dimmedDisabled: 'dimmed-disabled',
  invisibleDisabled: 'invisible-disabled',
  signOff: 'sign-off',
} as const;

export const USER_PILLARS = {
  valueChainAssessment: 'value-chain-assessment',
  materialityAssessment: 'materiality-assessment',
  settings: 'settings',
  taskManagement: 'task-management',
} as const;

export const NUMBER_FORMATS = {
  percent: 'percent',
  currency: 'currency',
  decimal: 'decimal',
  number: 'number',
} as const;

export const USER_PILLAR_MODULES = {
  [USER_PILLARS.valueChainAssessment]: {
    entityEditing: 'value-chain-assessment/entity-editing',
    entityLinking: 'value-chain-assessment/entity-linking',
    valueChainMapManagement: 'value-chain-assessment/value-chain-map-management',
  },
  [USER_PILLARS.materialityAssessment]: {
    entityEditing: 'materiality-assessment/entity-editing',
    entityLinking: 'materiality-assessment/entity-linking',
  },
  [USER_PILLARS.settings]: {
    userManagement: 'settings/user-management',
    multiLingual: 'settings/multi-lingual',
    uiConfiguration: 'settings/ui-configuration',
    dataSetsManagement: 'settings/data-sets-management',
  },
} as const;

export const FILTER_TABS = {
  organizational: 'organizational',
  geographical: 'geographical',
} as const;

export const PROFIT_CENTERS_TYPES = {
  SUBSIDIARY: 'Subsidiary',
  BUSINESS_UNIT: 'Business Unit',
  JV: 'JV',
  ASSOCIATE_SUBSIDIARY: 'Associate Subsidiary',
  DIVISION: 'Division',
} as const;

export const VALUE_CHAIN_CONNECTION_TYPES = {
  LOGICAL: 'logical',
  SUPPLIER_OF_GOODS: 'supplierOfGoods',
  SERVICE: 'service',
  MANUFACTURE: 'manufacture',
} as const;

export const CONNECTION_TYPES = {
  lConToUnits: VALUE_CHAIN_CONNECTION_TYPES.LOGICAL,
  sConToUnits: VALUE_CHAIN_CONNECTION_TYPES.SERVICE,
  gsConToUnits: VALUE_CHAIN_CONNECTION_TYPES.SUPPLIER_OF_GOODS,
  mConToUnits: VALUE_CHAIN_CONNECTION_TYPES.MANUFACTURE,
} as const;

export const COMPANY_MAP_TABS = {
  BUSINESS_STRUCTURE: 'businessStructure',
  VALUE_CHAIN_MAP: 'valueChainMap',
} as const;

export const TOOLTIP_POSITIONS = {
  TOP: 'top',
  BOTTOM: 'bottom',
};

export const COMPANY_MAP_MODALS = {
  RENAME: 'rename',
};

export const BUSINESS_STRUCTURE_MODALS = {
  CONFIRM_DISABLE: 'confirmDisable',
  DISABLED_INFO: 'disabledInfo',
  CONFIRM_STRUCTURE_CHANGE: 'confirmStructureChange',
};

export const VALUE_CHAIN_MAP_MODALS = {
  RENAME: 'rename',
  ADD_SWIMLANE: 'addSwimlane',
  REFUSED_DELETE_SWIMLANE: 'refusedDeleteSwimlane',
  VALUE_CONNECTION: 'valueConnection',
  ADD_PROFIT_CENTER: 'addProfitCenter',
  ADD_CUSTOMER: 'addCustomer',
  ADD_SUPPLIER: 'addSupplier',
  ADD_BALANCE_SHEET_ASSET: 'addBalanceSheetAsset',
  ADD_INPUT_PRODUCT: 'addInputProduct',
};

export const VALUE_CHAIN_MAP_CUSTOM_NODES = {
  UNIT: 'unit',
  SWIMLANE: 'swimlane',
  BOARD: 'board',
  GROUP: 'instance-group',
  GROUP_MORE: 'instance-group-more',
  CUSTOM: 'custom',
  DEFAULT: 'default',
  PROFIT_CENTER: 'profit-center',
  CUSTOMER: 'customer',
  SUPPLIER: 'supplier',
  OWN_OPERATION: 'ownOperation',
  INPUT_PRODUCT: 'product',
  BALANCE_SHEET_ASSET: 'balance-sheet-asset',
};

export const VALUE_CHAIN_PANEL_NODES_TYPES = {
  FILTER: 'filter',
  AI: 'ai',
  SWIMLANE: 'swimlane',
  OWN_OPERATION: 'ownOperation',
  PROFIT_CENTER: 'profitCenter',
  INPUT_PRODUCT: 'product',
  SUPPLIER: 'supplier',
  CUSTOMER: 'customer',
  BALANCE_SHEET_ASSET: 'balanceSheetAsset',
  SCENARIO: 'scenario',
};

export const VALUE_CHAIN_UNIT_TYPES = {
  PROFIT_CENTER: 'profit-center',
  SUPPLIER: 'supplier',
  CUSTOMER: 'customer',
  PRODUCT: 'product',
  OWN_OPERATION: 'own-operation',
  BALANCE_SHEET_ASSET: 'balance-sheet-asset',
  OUTPUT_PRODUCT: 'output-product',
};

export const VALUE_CHAIN_PRODUCT_TYPES = {
  INPUT: 'Input',
  OUTPUT: 'Output',
} as const;

export const BUSINESS_UNIT_TYPES = {
  LOGICAL: 'Logical',
  PHYSICAL: 'Physical',
};

export const TRANSPORT_METHODS = {
  AIR: 'air',
  ARTIC_TRUCK: 'articTruck',
  RAIL: 'rail',
  RIGID_TRUCK: 'rigidTruck',
  SEA: 'sea',
  TRUCK: 'truck',
  VAN: 'van',
};

export const DELETE_MODAL_TITLES = {
  DISCARD_DRAFT_REVIEW: 'discardDraftReview',
  DELETE_CONFIRMATION: 'deleteConfirmation',
  DELETE_SWIMLANE: 'deleteValueChainMapSwimlane',
  DELETE_TASK: 'deleteTaskModalTitle',
  DELETE_MA: 'deleteMA',
  DELETE_STANDARD_TOPIC: 'deleteStandardTopic',
  DELETE_TOPIC_HINT: 'hintDeleteStandardTopic',
  DELETE_STANDARD_SUBTOPIC: 'deleteStandardSubtopic',
  DELETE_STANDARD_SUBTOPIC_HINT: 'hintDeleteStandardSubtopic',
  DELETE_SECTOR_SPECIFIC: 'deleteSpecific',
  DELETE_SECTOR_SPECIFIC_HINT: 'hintDeleteSpecific',
  DELETE_COMPETITOR: 'deleteCompetitor',
  DELETE_COMPETITOR_HINT: 'hintDeleteCompetitor',
  DELETE_DATA_IMPACT: 'deleteImpact',
  DELETE_RECORD: 'deleteRecord',
  FINISH_MA: 'finishMA',
};

export const TASK_STATUS = {
  created: 'created',
  assigned: 'assigned',
  inProgress: 'in-progress',
  completed: 'completed',
  deferred: 'deferred',
  blocked: 'blocked',
  cancelled: 'cancelled',
} as const;

export const STATUS_MA = {
  READY_FOR_REVIEW: 'ready_for_review',
  DRAFT: 'draft',
  IN_PROGRESS: 'in_progress',
  APPROVED: 'approved',
};

export type TUsersRole = typeof USERS_ROLE[keyof typeof USERS_ROLE];
export type TTheme = typeof THEME[keyof typeof THEME];
export type TInputTypes = typeof INPUT_TYPES[keyof typeof INPUT_TYPES];
export type TTranslateStatus = typeof TRANSLATE_STATUS[keyof typeof TRANSLATE_STATUS];
export type TButtonVariant = typeof BUTTON_VARIANT[keyof typeof BUTTON_VARIANT];
export type TSeparatorVariant = typeof SEPARATOR_VARIANT[keyof typeof SEPARATOR_VARIANT];
export type TIconButtonSizes = typeof ICON_BUTTON_SIZES[keyof typeof ICON_BUTTON_SIZES];
export type TStatus = typeof STATUS[keyof typeof STATUS];
export type TValueChainStatus = typeof VALUE_CHAIN_STATUS[keyof typeof VALUE_CHAIN_STATUS];
export type TUserStatus = typeof USER_STATUS[keyof typeof USER_STATUS];
export type TPillarAccessTypes = typeof PILLAR_ACCESS_TYPES[keyof typeof PILLAR_ACCESS_TYPES];
export type TUserPillarAccessTypes = typeof USER_PILLAR_ACCESS_TYPES[keyof typeof USER_PILLAR_ACCESS_TYPES];
export type TCompanyStatus = typeof COMPANY_STATUS[keyof typeof COMPANY_STATUS];
export type TPresignedFileTypes = typeof PRESIGNED_FILE_TYPES[keyof typeof PRESIGNED_FILE_TYPES];
export type TOrders = typeof ORDERS[keyof typeof ORDERS];
export type TOrderByIdentifier = string;
export type TUserRoleTypes = typeof USER_ROLE_TYPES[keyof typeof USER_ROLE_TYPES];
export type TMfaEndpoints = typeof MFA_ENDPOINTS[keyof typeof MFA_ENDPOINTS];
export type TAuthenticatorSetupModalSteps = typeof AUTHENTICATOR_SETUP_MODAL_STEPS[
  keyof typeof AUTHENTICATOR_SETUP_MODAL_STEPS
];
export type TUserPillars = typeof USER_PILLARS[keyof typeof USER_PILLARS] | string; // TODO: Remove string
export type TNumberFormats = typeof NUMBER_FORMATS[keyof typeof NUMBER_FORMATS];
export type TDataSetEnumTypes = typeof DATA_SETS_ENUM_TYPES[keyof typeof DATA_SETS_ENUM_TYPES];
export type TUidNameTuples = typeof UID_NAME_TUPLES[keyof typeof UID_NAME_TUPLES];
export type TEnumTypes = typeof DATA_SETS_ENUM_TYPES[keyof typeof DATA_SETS_ENUM_TYPES];
export type TProfitCentersType = typeof PROFIT_CENTERS_TYPES[keyof typeof PROFIT_CENTERS_TYPES];

export type TFilterTabs = typeof FILTER_TABS[keyof typeof FILTER_TABS];
export type TCompanyMapTab = typeof COMPANY_MAP_TABS[keyof typeof COMPANY_MAP_TABS];
export type TTooltipPositions = typeof TOOLTIP_POSITIONS[keyof typeof TOOLTIP_POSITIONS];
export type TCompanyMapModal = typeof COMPANY_MAP_MODALS[keyof typeof COMPANY_MAP_MODALS];
export type TConnectionType = typeof VALUE_CHAIN_CONNECTION_TYPES[keyof typeof VALUE_CHAIN_CONNECTION_TYPES];
export type TBusinessStructureModal = typeof BUSINESS_STRUCTURE_MODALS[keyof typeof BUSINESS_STRUCTURE_MODALS];
export type TValueChainMapCustomNode = typeof VALUE_CHAIN_MAP_CUSTOM_NODES[keyof typeof VALUE_CHAIN_MAP_CUSTOM_NODES];
export type TValueChainMapModal = typeof VALUE_CHAIN_MAP_MODALS[keyof typeof VALUE_CHAIN_MAP_MODALS];
export type TValueChainPanelNodesType =
  typeof VALUE_CHAIN_PANEL_NODES_TYPES[keyof typeof VALUE_CHAIN_PANEL_NODES_TYPES];
export type TValueChainUnitType = typeof VALUE_CHAIN_UNIT_TYPES[keyof typeof VALUE_CHAIN_UNIT_TYPES];
export type TypesStepsProfitCenterModal = typeof STEPS_PROFIT_CENTER_MODAL[keyof typeof STEPS_PROFIT_CENTER_MODAL];
export type TBusinessUnitType = typeof BUSINESS_UNIT_TYPES[keyof typeof BUSINESS_UNIT_TYPES];
export type TTransportMethod = typeof TRANSPORT_METHODS[keyof typeof TRANSPORT_METHODS];
export type TTaskStatus = typeof TASK_STATUS[keyof typeof TASK_STATUS];

export type TUserPillarModules =
    | 'settings/user-management'
    | 'settings/multi-lingual'
    | 'settings/ui-configuration'
    | 'settings/data-sets-management'
    | 'value-chain-assessment/entity-editing'
    | 'value-chain-assessment/entity-linking'
    | 'value-chain-assessment/value-chain-map-management'
    | 'materiality-assessment/entity-editing'
    | 'materiality-assessment/entity-linking'
    | 'task-management/task-management'
    | 'materiality-assessment/materiality-assessment-management';

export type TImportErrorsModalMappingModalSubmit = 'error' | 'replace' | 'ignore';
